import React from 'react';
import PropTypes from 'prop-types';

/**
 * StandardTable - Ustandaryzowany komponent tabeli używany w całej aplikacji
 * 
 * @param {Array} columns - Definicje kolumn [{ key, title, render, headerClassName, cellClassName }]
 * @param {Array} data - Dane do wyświetlenia
 * @param {Function} onRowClick - Handler kliknięcia wiersza
 * @param {boolean} loading - Stan ładowania
 * @param {React.Component} SkeletonRow - Komponent skeleton dla stanu ładowania
 * @param {React.Component} EmptyState - Komponent pustego stanu
 * @param {Object} emptyStateProps - Propsy przekazywane do EmptyState
 * @param {boolean} selectable - Czy wiersze są wybieralne
 * @param {Set} selectedItems - Zbiór wybranych ID
 * @param {Function} onSelectItem - Handler wyboru pojedynczego elementu
 * @param {Function} onSelectAll - Handler wyboru wszystkich
 * @param {string} firstColumnTitle - Tytuł pierwszej kolumny (gdy selectable)
 * @param {string} className - Dodatkowe klasy CSS
 */
const StandardTable = ({
  columns = [],
  data = [],
  onRowClick,
  loading = false,
  SkeletonRow,
  EmptyState,
  emptyStateProps = {},
  selectable = false,
  selectedItems = new Set(),
  onSelectItem,
  onSelectAll,
  firstColumnTitle = '',
  className = '',
  getRowClassName = null,
  ...rest
}) => {
  const renderTableHeader = () => (
    <thead className="bg-white">
      <tr>
        {selectable && (
          <th scope="col" className="py-3.5 !pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
            <div className="flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={data.length > 0 && selectedItems.size === data.length}
                onChange={onSelectAll}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = selectedItems.size > 0 && selectedItems.size < data.length;
                  }
                }}
              />
              <span className="ml-4">{firstColumnTitle || columns[0]?.title || ''}</span>
            </div>
          </th>
        )}
        {columns.map((column, index) => {
          // Skip first column if selectable (it's handled above)
          if (selectable && index === 0) return null;
          
          return (
            <th
              key={column.key || index}
              scope="col"
              className={column.headerClassName || "px-3 py-3.5 text-left text-sm font-semibold text-gray-900"}
              style={column.headerStyle}
            >
              {column.title}
            </th>
          );
        })}
        {/* Empty column for actions if needed */}
        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0 text-right">
        </th>
      </tr>
    </thead>
  );

  const renderTableBody = () => {
    if (loading && SkeletonRow) {
      return (
        <tbody>
          {Array.from({ length: data.length || 5 }).map((_, index) => (
            <SkeletonRow key={index} />
          ))}
        </tbody>
      );
    }

    if (!loading && data.length === 0 && EmptyState) {
      return (
        <tbody>
          <tr>
            <td colSpan={columns.length + (selectable ? 1 : 0) + 1} className="p-0">
              <EmptyState {...emptyStateProps} />
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody className="bg-white divide-y divide-gray-200">
        {data.map((item, index) => {
          const itemId = item.id || item.link || index;
          const isSelected = selectedItems.has(itemId);
          const rowClassName = getRowClassName ? getRowClassName(item) : '';
          const isEven = index % 2 === 0;
          
          return (
            <tr
              key={itemId}
              className={`${
                isEven ? 'bg-white' : 'bg-gray-50'
              } ${
                onRowClick && !loading && !item.disabled
                  ? 'hover:bg-neutral-100 cursor-pointer'
                  : item.disabled
                  ? 'opacity-60 cursor-not-allowed'
                  : ''
              } ${rowClassName}`}
              onClick={(e) => {
                if (e.target.type === 'checkbox') return;
                if (onRowClick && !loading && !item.disabled) {
                  onRowClick(item);
                }
              }}
            >
              {selectable && (
                <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-0">
                  <div className="flex items-center ml-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={isSelected}
                      onChange={() => onSelectItem && onSelectItem(itemId)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="ml-4">
                      {columns[0]?.render ? columns[0].render(item) : item[columns[0]?.key]}
                    </div>
                  </div>
                </td>
              )}
              {columns.map((column, colIndex) => {
                // Skip first column if selectable (it's handled above)
                if (selectable && colIndex === 0) return null;
                
                return (
                  <td
                    key={column.key || colIndex}
                    className={column.cellClassName || "whitespace-nowrap px-3 py-5 text-sm text-gray-500"}
                    style={column.cellStyle}
                  >
                    {column.render ? column.render(item) : item[column.key]}
                  </td>
                );
              })}
              <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                {item.actions && item.actions}
              </td>
            </tr>
          );
        })}
      </tbody>
    );
  };

  return (
    <div className={`divide-y divide-gray-100 mt-6 ${className}`} {...rest}>
      <div className="markdown-table-container overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 inline-block min-w-full sm:px-6 lg:px-8">
          <div className="overflow-hidden border-b border-gray-200">
            <table className="min-w-full divide-y divide-gray-300">
              {renderTableHeader()}
              {renderTableBody()}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

StandardTable.displayName = 'StandardTable';

StandardTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    render: PropTypes.func,
    headerClassName: PropTypes.string,
    cellClassName: PropTypes.string,
    headerStyle: PropTypes.object,
    cellStyle: PropTypes.object,
  })).isRequired,
  data: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
  loading: PropTypes.bool,
  SkeletonRow: PropTypes.elementType,
  EmptyState: PropTypes.elementType,
  emptyStateProps: PropTypes.object,
  selectable: PropTypes.bool,
  selectedItems: PropTypes.instanceOf(Set),
  onSelectItem: PropTypes.func,
  onSelectAll: PropTypes.func,
  firstColumnTitle: PropTypes.string,
  className: PropTypes.string,
  getRowClassName: PropTypes.func,
};

export default StandardTable;
